import { useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ProjectCustomerRef } from '../../lib/api/domains/projects';

function label(c: { name: string; customerNumber?: string | null }): string {
  return c.customerNumber ? `${c.name} (${c.customerNumber})` : c.name;
}

/** Kundensuche über den Kunden-Hub — für alle Techniker freigegeben, liefert die lokale Kunden-ID. */
export function ProjectCustomerPicker({
  value,
  onChange,
  label: fieldLabel = 'Kunde',
  description,
}: {
  value: ProjectCustomerRef | null;
  onChange: (customer: ProjectCustomerRef | null) => void;
  label?: string;
  description?: string;
}) {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const { data, isFetching } = useQuery({
    queryKey: ['project-customer-search', debounced],
    queryFn: () => api.searchHubCustomers(debounced, 1, 20),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const found = useMemo(
    () =>
      (data?.customers ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        customerNumber: c.customerNumber ?? null,
      })),
    [data],
  );
  // Der gewählte Kunde muss in den Optionen stehen, sonst zeigt das Feld nach dem
  // Wählen wieder leer an, sobald sich die Suchtreffer ändern.
  const options = useMemo(() => {
    const list = value && !found.some((c) => c.id === value.id) ? [value, ...found] : found;
    return list.map((c) => ({ value: c.id, label: label(c) }));
  }, [found, value]);

  return (
    <Select
      label={fieldLabel}
      description={description}
      placeholder="Kunde suchen…"
      searchable
      clearable
      value={value?.id ?? null}
      onChange={(id) => onChange(id ? ([value, ...found].find((c) => c?.id === id) ?? null) : null)}
      searchValue={search}
      onSearchChange={setSearch}
      data={options}
      filter={({ options: opts }) => opts}
      nothingFoundMessage={
        debounced.trim().length < 2 ? 'Mind. 2 Zeichen tippen' : isFetching ? 'Suche…' : 'Kein Kunde gefunden'
      }
    />
  );
}
